import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { EntityTypeEnum, FeaturesEnum, LabelPreviewWithoutTypeOutDTO, LovDTO, TmplV2DetailOutDTO } from '@api/ringil-api.v2.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import Card from '@components/framework/card/Card.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { InputNumber } from '@components/framework/input/InputNumber';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import RowButton from '@components/RingilComponents/RowButton.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import RingilSpinner from '@components/ringil3/RingilSpinner/RingilSpinner';
import { HorizontalLine } from '@components/ringil3/styles.tsx';
import SvgRoute from '@components/ringil3/icons/heading/Route.tsx';
import SvgMapPin from '@icons/map-pin.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import { useCreateUpdateExpeditionNameSidebar } from '@components/ringil3/Features/Expedition/Sidebars/UpdateExpeditionNameSidebar.tsx';
import ContractedCarriersSection from '@components/ringil3/Features/Contracted/ContractedCarriersSection.tsx';
import { expeditionConfig } from '@components/framework/orders/validation.ts';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation.tsx';
import useForm, { Form } from '@hooks/useForm/useForm.tsx';
import useBEValidation from '@hooks/useBEValidation/useBEValidation.tsx';
import { findErrorMessage } from '@utils/validationUtils.ts';
import AttachmentsSection from '@app/pages/attachment/AttachmentsSection.tsx';
import RouteIssuesMessage from '@app/pages/expedition/create/components/RouteIssuesMessage.tsx';
import InternalInfoSection from '@app/pages/expedition/create/components/InternalInfoSection.tsx';
import { useRouteEditor } from '@app/pages/expedition/create/hooks/useRouteEditor.ts';
import { NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import LineHeader from '@app/pages/customer/LineEdit/components/LineHeader.tsx';
import LineRouteStop from '@app/pages/customer/LineEdit/components/LineRouteStop.tsx';
import { LineForm } from '@app/pages/customer/LineEdit/types.ts';
import { buildLineValidationSchema } from '@app/pages/customer/LineEdit/lineValidation.ts';
import { lineToSaveRequest, newLineInitialData } from '@app/pages/customer/LineEdit/lineToSaveRequest.ts';
import { detailToLineForm, LineFormLookups } from '@app/pages/customer/LineEdit/detailToLineForm.ts';

interface ComponentProps {
  /** Edited line; absent = a new one. */
  lineId?: string;
}

/**
 * Create / edit a Linka (Template 2.0 of kind LINE): the shipment header, stops with candidate
 * locations, orders and weekly windows, attachments, the contracted carriers and the internal info.
 *
 * Built from the create-expedition pieces where they fit as they are (orders panel, route editor,
 * attachments, internal info); the location card and the header are Linka-specific.
 */
const LineEdit: React.FC<ComponentProps> = ({ lineId }) => {
  const api = useContext(ApiContext);
  const { createTmplV2, updateTmplV2, getTmplV2Detail } = api;
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const { showToastMessage } = useContext(ToastContext);
  const { loggedUser, hasFeature } = useContext(AuthenticatedUserContext);
  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
  const { UpdateExpeditionNameSidebar, setVisible: setNameSidebarVisible } = useCreateUpdateExpeditionNameSidebar();

  const [loading, setLoading] = useState(!!lineId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const defaultCurrency = loggedUser?.defaultCurrencyIsoCode ?? 'EUR';
  const levelPrefix = tr('PriceTable.levelPrefix', 'Úroveň');
  const ordersConfig = expeditionConfig(!hasFeature([FeaturesEnum.NOT_MANDATORY_DIMENSIONS]));

  const form = useForm<LineForm>(buildLineValidationSchema({ tr, ordersConfig }), newLineInitialData(tr('LineEdit.newLine', 'Nová linka'), defaultCurrency, levelPrefix), d => save(d), false, false);

  // The shared route pieces are typed on the expedition form; the Linka form is a superset of it.
  const expeditionForm = form as unknown as Form<NewExpeditionForm>;
  const route = useRouteEditor(expeditionForm);

  const formRef = useRef(form);
  formRef.current = form;
  const setAttachments = useCallback((attachments: LineForm['attachments']) => formRef.current.setFieldValue('attachments', attachments), []);

  // Linky exist only with CONTRACTED_CARRIERS; a direct URL without it goes back to the templates
  const linesEnabled = hasFeature([FeaturesEnum.CONTRACTED_CARRIERS]);
  useEffect(() => {
    if (loggedUser && !linesEnabled) nav.navigate(nav.urlFunctions.createCustomerExpeditionTemplates());
  }, [loggedUser, linesEnabled]);

  useEffect(() => {
    if (!lineId || !linesEnabled) return;
    setLoading(true);

    const call = <T,>(fn: (onSuccess: (data: T) => void) => void) => new Promise<T>(resolve => fn(resolve));
    // a failed lookup only costs the names it would show, never the editor (the error toast still shows)
    const errorWithToast = (done: () => void) => (_e: any, showStandardError: () => void) => {
      showStandardError();
      done();
    };
    const lookup = <T,>(fn: (onSuccess: (data: T) => void, onError: () => void) => void, fallback: T) =>
      new Promise<T>(resolve => fn(data => resolve(data ?? fallback), () => resolve(fallback)));
    Promise.all([
      call<TmplV2DetailOutDTO>(ok =>
        getTmplV2Detail(lineId, ok, null, {
          onError: (_e, showStandardError) => {
            showStandardError();
            nav.navigate(nav.urlFunctions.createCustomerLines());
          },
        }),
      ),
      lookup<LovDTO[]>((ok, err) => api.cuLovResponsiblePersons(ok, null, { onError: errorWithToast(err) }), []),
      lookup<LovDTO[]>((ok, err) => api.lovCompanyFolders(null, EntityTypeEnum.EXPEDITION, ok, null, { onError: errorWithToast(err) }), []),
      lookup<LabelPreviewWithoutTypeOutDTO[]>((ok, err) => api.getAllLabelsForCompanyEntityType(EntityTypeEnum.EXPEDITION, '', ok, null, { onError: errorWithToast(err) }), []),
      lookup<LovDTO[]>((ok, err) => api.cuLovPartners('', ok, null, { onError: errorWithToast(err) }), []),
    ]).then(([detail, persons, folders, labels, partners]) => {
      const lookups: LineFormLookups = { persons: persons ?? [], folders: folders ?? [], labels: labels ?? [], partners: partners ?? [] };
      form.overwriteValues(detailToLineForm(detail, lookups, { currency: defaultCurrency, levelPrefix }));
      setLoading(false);
    });
  }, [lineId, linesEnabled]);

  const goToList = () => nav.navigate(nav.urlFunctions.createCustomerLines());

  const onSubmit = () => {
    setSubmitAttempted(true);
    if (route.orderIssues.length > 0) {
      showToastMessage(
        tr('LineEdit.routeIssuesToastTitle', 'Chyba'),
        tr('LineEdit.routeIssuesToastMessage', 'Některé objednávky nejsou správně naloženy a vyloženy. Opravte trasu před uložením linky.'),
        'error',
      );
      return;
    }
    form.validateAndSend();
  };

  const save = (data: LineForm) => {
    setIsSubmitting(true);
    const request = lineToSaveRequest(data);
    const callbacks = {
      onValidationFailed: (errors: any) => {
        setBeValidationErrors(errors);
        setIsSubmitting(false);
      },
      onError: (_error: any, showStandardError: () => void) => {
        showStandardError();
        setIsSubmitting(false);
      },
    };
    const onSaved = () => {
      setIsSubmitting(false);
      showToastMessage(tr('generic.success', 'Success'), tr('LineEdit.saved', 'Linka {{name}} byla uložena', { name: data.name }), 'success');
      goToList();
    };
    if (lineId) updateTmplV2(lineId, request, onSaved, null, callbacks);
    else createTmplV2(request, onSaved, null, callbacks);
  };

  if (loading) return <RingilSpinner />;

  const locations = form.values.locations;
  const carriersError = findErrorMessage(form.validationErrors, 'contractedCarriers', tr);
  const intervalError = findErrorMessage(form.validationErrors, 'intervalHours', tr);
  const priceTableError = findErrorMessage(form.validationErrors, 'priceTable', tr);
  const bothDirectionsError = findErrorMessage(form.validationErrors, 'bothDirections', tr);

  return (
    <div className={'flex flex-col gap-2'}>
      <BreadCrumbComponent
        items={[
          { label: tr('LineEdit.breadcrumbTemplates', 'Šablony'), url: nav.createNavigationLink(nav.urlFunctions.createCustomerLines()) },
          { label: lineId ? form.values.name : tr('LineEdit.newLine', 'Nová linka') },
        ]}
      />
      <BackendValidationComponent />

      <LineHeader form={form} companyId={loggedUser?.companyId} hasFeature={hasFeature} onEditName={() => setNameSidebarVisible(true)} />

      <SectionPanel icon={<SvgRoute />} heading={tr('LineEdit.route', 'Trasa')}>
        <div className={'flex flex-col gap-1 mx-4 mb-2'}>
          <RingilInputSwitch
            label={tr('LineEdit.bothDirections', 'Obousměrná linka')}
            value={!!form.values.bothDirections}
            onChange={v => form.setFieldValue(form.names.bothDirections, v)}
            dataTestId={'line-both-directions'}
          />
          <span className={'text-xs text-content-secondary ml-12'}>
            {tr('LineEdit.bothDirectionsHint', 'Smluvní ceny platí pro oba směry. Začátek a konec trasy stačí zadat jednou.')}
          </span>
          {bothDirectionsError && <span className={'text-sm text-complementary-error'}>{bothDirectionsError}</span>}
        </div>
        <RouteIssuesMessage issues={submitAttempted ? route.orderIssues : []} />
        {locations.map((stop, locIndex) => (
          <LineRouteStop
            key={stop.id}
            form={form}
            stop={stop}
            locIndex={locIndex}
            ordersConfig={ordersConfig}
            companyId={loggedUser?.companyId}
            canDeleteLocation={route.canDeleteLocation}
            onDeleteLocation={route.deleteLocation}
            onMoveStop={route.moveStop}
            onAddStopAbove={route.addStopAbove}
            onDeleteUnloading={route.deleteUnloadingLocation}
            unloadableOrders={route.getAllUnloadableOrders(locIndex)}
            onUnloadOrder={route.unloadOrder}
            onOrderAdded={route.syncOrdersToLastLocations}
            onMoveOrderLoading={route.moveOrderLoading}
            orderIssues={route.orderIssues}
          />
        ))}
        <RowButton
          title={tr('LineEdit.addMidStop', 'Mezizastávka')}
          icon={<SvgMapPin width={24} height={32} className={'text-content-quaternary'} />}
          onClick={() => route.addStopAbove(locations.length - 1)}
          dataTestId={'line-addStop'}
        />

        <AttachmentsSection attachments={form.values.attachments} onChange={setAttachments} />

        <HorizontalLine variant={'tertiary'} />

        <Card>
          <div className={'flex flex-col gap-4'}>
            <div className={'flex flex-row items-center justify-between'}>
              <COHeadline variant={'h3'} title={tr('LineEdit.contractedCarrierSection', 'Smluvní dopravce')} />
              {form.values.contractedCarriers.length > 0 && (
                <RingilButton
                  mode={'table'}
                  icon={<SvgDelete />}
                  tooltip={tr('LineEdit.removeAllCarriers', 'Odebrat všechny dopravce')}
                  onClick={() => form.setFieldValue(form.names.contractedCarriers, [])}
                />
              )}
            </div>
            <div className={'flex flex-row flex-wrap items-end gap-6'}>
              <Field label={tr('LineEdit.intervalHours', 'Platnost poptávky (hod.)')} required errorMessage={intervalError} width={'220px'}>
                <InputNumber
                  value={form.values.intervalHours}
                  min={1}
                  maxFractionDigits={0}
                  onValueChange={e => form.setFieldValue(form.names.intervalHours, e.value)}
                />
              </Field>
              {!!form.values.capacity?.ladenFactor && (
                <InputCheckbox
                  value={form.values.allowPartialDelivery}
                  label={tr('LineEdit.allowPartialDelivery', 'Povolit částečné plnění')}
                  note={tr('LineEdit.allowPartialDeliveryNote', 'Dopravce může přijmout jen část jednotek')}
                  onChange={(_, v) => form.setFieldValue(form.names.allowPartialDelivery, v)}
                />
              )}
            </div>
            <ContractedCarriersSection
              mode={'line'}
              table={form.values.priceTable}
              carriers={form.values.contractedCarriers}
              onChange={({ table, carriers }) => {
                form.setFieldValue(form.names.priceTable, table);
                form.setFieldValue(form.names.contractedCarriers, carriers);
              }}
              subjectName={form.values.name}
              errorMessage={[carriersError, priceTableError].filter(Boolean).join(' ') || undefined}
            />
          </div>
        </Card>

        <InternalInfoSection form={expeditionForm} mode={'LINE'} />

        <div className={'flex flex-row gap-4 mx-4 my-3'}>
          <RingilButton mode="filled" size="big" label={tr('LineEdit.save', 'Uložit linku')} onClick={onSubmit} loading={isSubmitting} dataTestId="line-save" />
          <RingilButton mode="link" size="big" label={tr('LineEdit.cancel', 'Zrušit')} onClick={goToList} dataTestId="line-cancel" />
        </div>
      </SectionPanel>

      <UpdateExpeditionNameSidebar currentName={form.values.name} onContinue={name => form.setFieldValue('name', name)} />
    </div>
  );
};

export default LineEdit;
